// PlansTopupsModal — org billing catalog popups.
// `catalog` shows Plans + Top-ups as tabs; `topups` shows only the top-up packs.
import { useEffect, useState } from 'react';
import { Modal, Spinner, Tabs } from '@/components/ui';
import {
  BillingCycleToggle,
  PlansCatalog,
  SalesSeatModal,
  TopupCatalog,
  usePricingCatalog,
} from './pricingCatalog';
import './plans-topups-modal.css';

export type PlansTopupsMode = 'catalog' | 'topups';

export interface PlansTopupsModalProps {
  open: boolean;
  onClose: () => void;
  mode: PlansTopupsMode;
}

export default function PlansTopupsModal({ open, onClose, mode }: PlansTopupsModalProps) {
  const catalog = usePricingCatalog(open);
  const [tab, setTab] = useState<'plans' | 'topups'>('plans');

  useEffect(() => {
    if (open) setTab(mode === 'topups' ? 'topups' : 'plans');
  }, [open, mode]);

  const title = mode === 'topups' ? 'Buy more credits' : 'Plans & top-ups';
  const showTabs = mode === 'catalog';
  const showPlans = mode === 'catalog' && tab === 'plans';
  const showTopups = mode === 'topups' || tab === 'topups';

  return (
    <>
      <Modal open={open} onClose={onClose} title={title} size="xl" className="plans-topups-modal">
        {showTabs && (
          <Tabs
            ariaLabel="Plans or top-ups"
            className="plans-topups-tabs"
            tabs={[
              { id: 'plans', label: 'Plans', icon: 'bi bi-tag', controls: 'plans-topups-plans' },
              {
                id: 'topups',
                label: 'Top-ups',
                icon: 'bi bi-coin',
                controls: 'plans-topups-topups',
              },
            ]}
            active={tab}
            onChange={(id) => setTab(id as 'plans' | 'topups')}
          />
        )}

        {showPlans && (
          <div className="plans-topups-panel" id="plans-topups-plans" role="tabpanel">
            <div className="plans-topups-intro">
              <p className="plans-topups-lede">
                Bring your own API keys — pay Quralyst for the intelligence layer.
              </p>
              <BillingCycleToggle catalog={catalog} />
            </div>
            {catalog.loading ? (
              <div className="org-tab-loading">
                <Spinner label="Loading plans" />
              </div>
            ) : (
              <PlansCatalog catalog={catalog} />
            )}
          </div>
        )}

        {showTopups && (
          <div
            className="plans-topups-panel"
            id="plans-topups-topups"
            role={showTabs ? 'tabpanel' : undefined}
          >
            <div className="plans-topups-intro">
              <p className="plans-topups-lede">
                One-time top-ups — never expire, consumed after your monthly plan credits.
              </p>
            </div>
            {catalog.loading ? (
              <div className="org-tab-loading">
                <Spinner label="Loading top-ups" />
              </div>
            ) : (
              <TopupCatalog catalog={catalog} />
            )}
          </div>
        )}

        {catalog.checkoutError && (
          <div className="alert alert-danger mt-3 mb-0" role="alert">
            {catalog.checkoutError}
          </div>
        )}
      </Modal>
      <SalesSeatModal catalog={catalog} />
    </>
  );
}
